import { useState, useEffect } from 'react'
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    IconButton,
    InputAdornment,
    Divider,
    Chip,
} from '@mui/material'
import {
    Visibility,
    VisibilityOff,
    CheckCircle,
    Error,
    ContentCopy,
    Refresh,
} from '@mui/icons-material'
import { whatsappApi } from '@api/whatsapp'
import { toast } from 'react-toastify'

/**
 * WhatsApp Integration Settings Component
 * Used within Form Settings dialog to configure WhatsApp Business API
 */
const WhatsAppSettings = ({ formId, language }) => {
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [showToken, setShowToken] = useState(false)

    const [config, setConfig] = useState({
        is_active: false,
        phone_number_id: '',
        phone_number: '',
        access_token: '',
        webhook_verify_token: '',
        business_account_id: '',
    })

    // Generate webhook URL
    const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`

    // Load config on mount
    useEffect(() => {
        if (formId) {
            loadConfig()
        }
    }, [formId])

    const loadConfig = async () => {
        try {
            setLoading(true)
            const response = await whatsappApi.getConfig(formId)
            if (response.success && response.data) {
                setConfig({
                    // Properly convert is_active: handle "1", 1, true, "true" etc
                    is_active: response.data.is_active === true || response.data.is_active === 1 || response.data.is_active === '1',
                    phone_number_id: response.data.phone_number_id || '',
                    phone_number: response.data.phone_number || '',
                    access_token: '', // Never prefill for security
                    webhook_verify_token: response.data.webhook_verify_token || '',
                    business_account_id: response.data.business_account_id || '',
                })
            }
        } catch (error) {
            // No config exists yet - that's OK
            console.log('No WhatsApp config found for this form')
        } finally {
            setLoading(false)
        }
    }

    const handleSave = async () => {
        if (!config.phone_number_id || !config.phone_number || !config.webhook_verify_token) {
            toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields')
            return
        }

        try {
            setSaving(true)
            const response = await whatsappApi.saveConfig(formId, config)
            if (response.success) {
                toast.success(language === 'ar' ? 'تم حفظ إعدادات WhatsApp' : 'WhatsApp settings saved')
                // Clear token from state after save (security)
                setConfig(prev => ({ ...prev, access_token: '' }))
            } else {
                toast.error(response.message || 'Failed to save')
            }
        } catch (error) {
            toast.error(error.message || 'Error saving settings')
        } finally {
            setSaving(false)
        }
    }

    const handleTestConnection = async () => {
        try {
            setTesting(true)
            setTestResult(null)
            const response = await whatsappApi.testConnection(formId)
            setTestResult({
                success: response.success,
                message: response.message || (response.success ? 'Connection successful!' : 'Connection failed')
            })
        } catch (error) {
            setTestResult({
                success: false,
                message: error.message || 'Connection test failed'
            })
        } finally {
            setTesting(false)
        }
    }

    const handleCopyWebhook = () => {
        navigator.clipboard.writeText(webhookUrl)
        toast.success(language === 'ar' ? 'تم نسخ الرابط' : 'URL copied to clipboard')
    }

    const generateVerifyToken = () => {
        const token = 'verify_' + Math.random().toString(36).substring(2, 15)
        setConfig(prev => ({ ...prev, webhook_verify_token: token }))
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        )
    }

    const isRTL = language === 'ar'

    return (
        <Box sx={{ pt: 1 }} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Info Alert */}
            <Alert severity="info" sx={{ mb: 3 }}>
                {language === 'ar'
                    ? 'قم بتوصيل هذا النموذج بـ WhatsApp Business API لاستقبال الرسائل والرد عليها.'
                    : 'Connect this form to WhatsApp Business API to receive and reply to messages.'}
            </Alert>

            {/* Enable Toggle */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={config.is_active}
                            onChange={(e) => setConfig(prev => ({ ...prev, is_active: e.target.checked }))}
                            color="primary"
                        />
                    }
                    label={
                        <Typography fontWeight={600}>
                            {language === 'ar' ? 'تفعيل تكامل WhatsApp' : 'Enable WhatsApp Integration'}
                        </Typography>
                    }
                />
            </Paper>

            {/* Configuration Fields */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                {language === 'ar' ? 'إعدادات API' : 'API Configuration'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Phone Number */}
                <TextField
                    fullWidth
                    label={language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                    value={config.phone_number}
                    onChange={(e) => setConfig(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="+963989335580"
                    helperText={language === 'ar' ? 'رقم WhatsApp Business' : 'Your WhatsApp Business number'}
                    required
                />

                {/* Phone Number ID */}
                <TextField
                    fullWidth
                    label={language === 'ar' ? 'معرف رقم الهاتف' : 'Phone Number ID'}
                    value={config.phone_number_id}
                    onChange={(e) => setConfig(prev => ({ ...prev, phone_number_id: e.target.value }))}
                    placeholder="123456789012345"
                    helperText={language === 'ar' ? 'من Meta Developer Console' : 'From Meta Developer Console'}
                    required
                />

                {/* Access Token */}
                <TextField
                    fullWidth
                    label={language === 'ar' ? 'رمز الوصول' : 'Access Token'}
                    type={showToken ? 'text' : 'password'}
                    value={config.access_token}
                    onChange={(e) => setConfig(prev => ({ ...prev, access_token: e.target.value }))}
                    placeholder="EAAxxxxxxx..."
                    helperText={language === 'ar'
                        ? 'سيتم تشفيره. اتركه فارغاً للاحتفاظ بالرمز الحالي.'
                        : 'Will be encrypted. Leave empty to keep existing token.'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowToken(!showToken)} edge="end">
                                    {showToken ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Business Account ID (optional) */}
                <TextField
                    fullWidth
                    label={language === 'ar' ? 'معرف حساب الأعمال (اختياري)' : 'Business Account ID (Optional)'}
                    value={config.business_account_id}
                    onChange={(e) => setConfig(prev => ({ ...prev, business_account_id: e.target.value }))}
                    placeholder="123456789012345"
                />

                <Divider sx={{ my: 1 }} />

                {/* Webhook Configuration */}
                <Typography variant="subtitle1" fontWeight={600}>
                    {language === 'ar' ? 'إعدادات Webhook' : 'Webhook Configuration'}
                </Typography>

                {/* Webhook URL (read-only) */}
                <TextField
                    fullWidth
                    label={language === 'ar' ? 'رابط Webhook' : 'Webhook URL'}
                    value={webhookUrl}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleCopyWebhook} edge="end">
                                    <ContentCopy />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    helperText={language === 'ar'
                        ? 'استخدم هذا الرابط في إعدادات Meta Developer'
                        : 'Use this URL in Meta Developer webhook settings'}
                />

                {/* Verify Token */}
                <TextField
                    fullWidth
                    label={language === 'ar' ? 'رمز التحقق' : 'Verify Token'}
                    value={config.webhook_verify_token}
                    onChange={(e) => setConfig(prev => ({ ...prev, webhook_verify_token: e.target.value }))}
                    placeholder="your_verify_token"
                    helperText={language === 'ar'
                        ? 'أي نص تختاره - استخدم نفس القيمة في Meta'
                        : 'Any string you choose - use same value in Meta'}
                    required
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={generateVerifyToken} edge="end" title="Generate">
                                    <Refresh />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Test Result */}
            {testResult && (
                <Alert
                    severity={testResult.success ? 'success' : 'error'}
                    sx={{ mt: 2 }}
                    icon={testResult.success ? <CheckCircle /> : <Error />}
                >
                    {testResult.message}
                </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={handleTestConnection}
                    disabled={testing || !config.phone_number_id}
                    startIcon={testing ? <CircularProgress size={16} /> : <CheckCircle />}
                >
                    {language === 'ar' ? 'اختبار الاتصال' : 'Test Connection'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving && <CircularProgress size={16} />}
                >
                    {language === 'ar' ? 'حفظ الإعدادات' : 'Save Settings'}
                </Button>
            </Box>

            {/* Setup Instructions */}
            <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {language === 'ar' ? 'خطوات الإعداد:' : 'Setup Steps:'}
                </Typography>
                <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0, '& li': { mb: 0.5 } }}>
                    <li>{language === 'ar' ? 'أنشئ تطبيق Meta Developer' : 'Create a Meta Developer App'}</li>
                    <li>{language === 'ar' ? 'أضف منتج WhatsApp' : 'Add WhatsApp product'}</li>
                    <li>{language === 'ar' ? 'سجل رقم هاتفك' : 'Register your phone number'}</li>
                    <li>{language === 'ar' ? 'انسخ Phone Number ID و Access Token' : 'Copy Phone Number ID and Access Token'}</li>
                    <li>{language === 'ar' ? 'أضف رابط Webhook أعلاه في إعدادات Meta' : 'Add the Webhook URL above in Meta settings'}</li>
                    <li>{language === 'ar' ? 'اشترك في أحداث messages' : 'Subscribe to messages events'}</li>
                </Typography>
            </Paper>
        </Box>
    )
}

export default WhatsAppSettings
